/**
 * Normalizes and validates a possibly scheme-less URL string.
 *
 * Accepts input with or without a scheme/www prefix (e.g. "bl.uk",
 * "www.bl.uk", "https://bl.uk"), defaults missing schemes to https,
 * and rejects anything that isn't http/https, that embeds credentials
 * (blocks the `https://trusted.com@evil.com` spoofing trick), or whose
 * hostname has no dot (rejects single-label input like "www7" that the
 * URL constructor accepts as syntactically valid but is never a real
 * public web address).
 *
 * @param {string} value
 * @returns {URL|null} the parsed URL, or null if it can't be safely resolved
 */
export function normalizeUrl(value) {
  if (typeof value !== 'string') return null;
  let trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('//')) {
    trimmed = `https:${trimmed}`;
  } else if (!/^[a-z][a-z\d+\-.]*:\/\//i.test(trimmed)) {
    trimmed = `https://${trimmed}`;
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (parsed.username || parsed.password) return null;
    if (!parsed.hostname.includes('.')) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Characters escaped before a target URL is spliced into the archive path:
 * the HTML metacharacters (< > " ' ` &) that carry meaning wherever the path
 * is later rendered, and the characters that would restructure the path
 * itself (# opens a fragment, \ is a path separator to browsers, plus the
 * unwise characters {}|^ and any whitespace).
 */
const ARCHIVE_PATH_UNSAFE = /[<>"'`&#\\{}|^\s]/g;

/** Percent-encodes one character as its UTF-8 bytes, e.g. "<" -> "%3C". */
function percentEncodeChar(char) {
  let encoded = '';
  for (const byte of new TextEncoder().encode(char)) {
    encoded += `%${byte.toString(16).toUpperCase().padStart(2, '0')}`;
  }
  return encoded;
}

/**
 * Builds the archive's wayback-style lookup path for a target URL.
 *
 * The route takes the target literally after the timestamp segment, so the
 * address is spliced in readable — ending `.../https://www.bl.uk/`, not the
 * percent-encoded `https%3A%2F%2Fwww.bl.uk%2F` that encodeURIComponent gives.
 * Keeping it readable means this function, not encodeURIComponent, owns the
 * escaping: every metacharacter that could change how the resulting path is
 * parsed or rendered is percent-encoded here, which leaves the scheme, host
 * and path separators intact. The literal `/archive/` prefix keeps the result
 * a same-origin path, and normalizeUrl has already rejected non-http(s)
 * schemes and embedded credentials.
 *
 * Note that `&` is escaped too, so a seed's query string reaches the archive
 * as one opaque value (`?a=1%26b=2`) instead of as extra parameters of the
 * archive request itself.
 *
 * The fragment is dropped before splicing. Escaping `#` to `%23` would turn the
 * fragment into ordinary path characters, and `new URL` normalises dot segments
 * in a path but not in a fragment — so `../` hidden after a `#` survives into
 * the result and the browser resolves it on navigation. Six or more segments
 * walked out of `/archive/` entirely (`.../#/../../../../../../en/ukwa/collection`
 * resolved to `/en/ukwa/collection`). A fragment is client-side only and never
 * reaches a server, so discarding it costs nothing and closes that off. `#`
 * stays in ARCHIVE_PATH_UNSAFE as defence in depth for callers passing a
 * pre-parsed URL object.
 *
 * @param {string|URL} value a raw input string, or an already-parsed URL
 * @returns {string|null} the archive path, or null if the URL can't be resolved
 */
export function archivePath(value) {
  const parsed = value instanceof URL ? value : normalizeUrl(value);
  if (!parsed) return null;
  // Drop the fragment — see above. Copy first: a caller passing a URL instance
  // must not have their object mutated as a side effect.
  const withoutHash = new URL(parsed.href);
  withoutHash.hash = '';
  const target = withoutHash.href.replace(ARCHIVE_PATH_UNSAFE, percentEncodeChar);
  return `/archive/*/${target}`;
}
